import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ShieldCheck,
  Lock,
  ArrowUpRight,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANTS = {
  starter_deposit: {
    eyebrow: "Reserve your slot",
    heading: "$99 deposit. Build kicks off within 48\u00a0hrs.",
    blurb:
      "Locks in your build slot and discovery call. The remaining build balance is only billed when your site goes live. Fully refundable if we can't ship a site you'd be proud to publish.",
    secureNote: "Card details never touch our servers. You'll be redirected to Stripe to complete the $99.00 deposit.",
    submitLabel: "Pay $99 deposit · Reserve slot",
  },
  visibility_setup: {
    eyebrow: "Local Visibility onboarding",
    heading: "$149 onboarding. Profile work starts right after.",
    blurb:
      "Covers your initial Google Business Profile setup or cleanup. Refundable any time before we start the work, and non-refundable once setup begins. The monthly service starts separately once setup is complete and we have account access and content to post.",
    secureNote: "Card details never touch our servers. You'll be redirected to Stripe to complete the $149.00 onboarding fee.",
    submitLabel: "Pay $149 onboarding fee",
  },
  gbp_setup: {
    eyebrow: "Google Business Profile · one-time",
    heading: "$299 once. No subscription, no website required.",
    blurb:
      "Covers creating a new Google Business Profile or auditing, cleaning up, and completing the one you have — one location. You keep ownership and grant us manager access; we never ask for your Google password. Verification is decided by Google and isn't guaranteed.",
    secureNote: "Card details never touch our servers. You'll be redirected to Stripe to complete the one-time $299.00 payment. Nothing recurring is created.",
    submitLabel: "Pay $299 · one-time",
  },
};

export default function ReserveModal({ open, onClose, packageId = "starter_deposit" }) {
  const variant = VARIANTS[packageId] || VARIANTS.starter_deposit;
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    business: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleReserve = async (e) => {
    e.preventDefault();
    if (!form.customer_email.trim()) {
      toast.error("We need an email to send the receipt.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
        customer_email: form.customer_email.trim() || undefined,
        customer_name: form.customer_name.trim() || undefined,
        business: form.business.trim() || undefined,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not start checkout. Try again.";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="reserve-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl bg-[#0e0e10] border border-white/[0.1] overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.85)]"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="reserve-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors z-10"
            >
              <X size={14} weight="bold" />
            </button>

            <div className="relative px-6 sm:px-8 pt-10 pb-6">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-700/15 blur-3xl pointer-events-none" />

              <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-2">
                <Lightning size={12} weight="fill" className="text-brand-400" />
                {variant.eyebrow}
              </div>

              <h3 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight text-white leading-[1.05]">
                {variant.heading}
              </h3>
              <p className="mt-3 text-zinc-400 text-[14.5px] leading-relaxed">
                {variant.blurb}
              </p>
            </div>

            <form
              onSubmit={handleReserve}
              noValidate
              data-testid="reserve-form"
              className="px-6 sm:px-8 pb-7 space-y-4"
            >
              <Field
                label="Email"
                testid="reserve-email"
                value={form.customer_email}
                onChange={update("customer_email")}
                placeholder="you@example.com"
                type="email"
                required
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Your name"
                  testid="reserve-name"
                  value={form.customer_name}
                  onChange={update("customer_name")}
                  placeholder="Greg Clark"
                />
                <Field
                  label="Business"
                  testid="reserve-business"
                  value={form.business}
                  onChange={update("business")}
                  placeholder="CleanCurrent LLC"
                />
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 flex items-start gap-3">
                <ShieldCheck size={18} weight="regular" className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-[13.5px]">Secure checkout via Stripe</div>
                  <div className="text-zinc-400 text-[12.5px] mt-0.5">
                    {variant.secureNote}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reserve-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
              >
                {loading ? "Redirecting to Stripe…" : variant.submitLabel}
                {!loading && <ArrowUpRight size={16} weight="bold" />}
              </button>

              <p
                data-testid="reserve-legal"
                className="text-center text-[11.5px] text-zinc-400 leading-relaxed"
              >
                By continuing, you agree to WebJesters'{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 underline underline-offset-4 hover:text-white"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 underline underline-offset-4 hover:text-white"
                >
                  Privacy Policy
                </a>
                .
              </p>

              <div className="flex items-center justify-center gap-2 text-[11.5px] text-zinc-400">
                <Lock size={11} weight="regular" />
                Powered by Stripe · 256-bit encryption
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.18em] uppercase text-zinc-400">
        {label}
        {required && <span className="text-brand-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-600 focus:outline-none focus:border-white/30 transition-colors"
      />
    </div>
  );
}
